import { useState, useEffect, useRef } from 'react';
import { FileSpreadsheet, FileText } from 'lucide-react';
import { supabase } from '@core/supabase/client';
import { Modal } from '@shared/components/ui/Modal';
import type { OrgUser } from '@core/types';
import type {
  ExtraColumnKey,
  BaseColumnKey,
  CandidatesTablePolicy,
  CandidateListUserAccess,
} from './candidatesListPreferences';
import {
  EXTRA_COLUMN_META,
  loadCandidatesTablePolicy,
  saveCandidatesTablePolicy,
  normalizeCandidateListUserAccess,
} from './candidatesListPreferences';

const BASE_COL_OPTIONS: { key: BaseColumnKey; label: string }[] = [
  { key: 'email', label: 'Email' },
  { key: 'current_title', label: 'Current title' },
  { key: 'current_company', label: 'Company' },
  { key: 'location', label: 'Location' },
  { key: 'source', label: 'Source' },
  { key: 'skills', label: 'Skills' },
  { key: 'rolefit', label: 'Role-fit' },
  { key: 'resume', label: 'Resume' },
  { key: 'created', label: 'Created when' },
];

export { ListOperationsDropdown as CandidatesOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';

export function CandidatesCustomizeColumnsModal({
  open,
  onClose,
  orgId,
  value,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  value: ExtraColumnKey[];
  onSave: (cols: ExtraColumnKey[]) => void;
}) {
  const [local, setLocal] = useState<ExtraColumnKey[]>(value);
  useEffect(() => {
    if (open) setLocal(value);
  }, [open, value]);

  if (!orgId) return null;

  return (
    <Modal open={open} onClose={onClose} title="Configure columns" subtitle="Add optional fields from the Candidate object" icon={<FileSpreadsheet className="w-4 h-4" />} size="md">
      <div className="space-y-2">
        {EXTRA_COLUMN_META.map(({ key, label }) => (
          <label key={key} className="flex items-center gap-2 text-sm vscode-text-primary cursor-pointer">
            <input
              type="checkbox"
              className="rounded"
              checked={local.includes(key)}
              onChange={() => {
                setLocal((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
              }}
            />
            {label}
          </label>
        ))}
        <div className="flex justify-end gap-2 pt-4">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              onSave(local);
              const p = loadCandidatesTablePolicy(orgId);
              p.extraColumns = local;
              saveCandidatesTablePolicy(orgId, p);
              onClose();
            }}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function CandidatesTableRightsModal({
  open,
  onClose,
  orgId,
  policy,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  policy: CandidatesTablePolicy;
  onSave: (p: CandidatesTablePolicy) => void;
}) {
  const [local, setLocal] = useState<CandidatesTablePolicy>(policy);
  const [users, setUsers] = useState<OrgUser[]>([]);

  useEffect(() => {
    if (open) setLocal(policy);
  }, [open, policy]);

  useEffect(() => {
    if (!open || !orgId) return;
    let cancelled = false;
    void supabase
      .from('ggj_b2b_org_users')
      .select('id, org_id, keycloak_sub, display_name, email, role, is_active, invited_at, joined_at')
      .eq('org_id', orgId)
      .eq('is_active', true)
      .order('display_name')
      .then(({ data }) => {
        if (!cancelled && data) setUsers(data as OrgUser[]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orgId]);

  if (!orgId) return null;

  const patchUserAccess = (userId: string, field: keyof CandidateListUserAccess, value: boolean) => {
    setLocal((prev) => {
      const cur = normalizeCandidateListUserAccess(prev.userFieldAccess[userId]);
      return {
        ...prev,
        userFieldAccess: {
          ...prev.userFieldAccess,
          [userId]: { ...cur, [field]: value },
        },
      };
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Manage Rights"
      subtitle="Column visibility, sensitive-data masking, and per-user access for this list"
      icon={<FileText className="w-4 h-4" />}
      size="xl"
    >
      <div className="space-y-4 text-sm max-h-[min(70vh,520px)] overflow-y-auto pr-1">
        <div>
          <p className="font-semibold vscode-text-primary mb-2">Org-wide masking</p>
          <label className="flex items-center gap-2 mb-1 cursor-pointer">
            <input
              type="checkbox"
              checked={local.maskEmail}
              onChange={(e) => setLocal((p) => ({ ...p, maskEmail: e.target.checked }))}
            />
            Mask email addresses (unless user is granted below)
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={local.maskPhone}
              onChange={(e) => setLocal((p) => ({ ...p, maskPhone: e.target.checked }))}
            />
            Mask phone numbers
          </label>
        </div>

        <div>
          <p className="font-semibold vscode-text-primary mb-2">Hide base columns</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {BASE_COL_OPTIONS.map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={local.hiddenBaseColumns.includes(key)}
                  onChange={() =>
                    setLocal((p) => ({
                      ...p,
                      hiddenBaseColumns: p.hiddenBaseColumns.includes(key)
                        ? p.hiddenBaseColumns.filter((k) => k !== key)
                        : [...p.hiddenBaseColumns, key],
                    }))
                  }
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div>
          <p className="font-semibold vscode-text-primary mb-2">Per-user access</p>
          <p className="text-[11px] vscode-text-tertiary mb-2">
            Auto-populated from active org members. Sensitive columns: unmask when org-wide masking is on. Record columns:
            intended policy for read / write / delete / child operations (enforce with server roles where applicable).
          </p>
          <div className="border border-[var(--vscode-border-primary)] rounded-lg overflow-x-auto">
            <table className="w-full text-xs min-w-[720px]">
              <thead>
                <tr className="vscode-bg-tertiary border-b border-[var(--vscode-border-primary)]">
                  <th className="text-left px-2 py-2 align-bottom" rowSpan={2}>
                    User
                  </th>
                  <th className="text-center px-1 py-1 border-l border-[var(--vscode-border-primary)]" colSpan={3}>
                    Unmask sensitive fields
                  </th>
                  <th className="text-center px-1 py-1 border-l border-[var(--vscode-border-primary)]" colSpan={4}>
                    Record permissions
                  </th>
                </tr>
                <tr className="vscode-bg-tertiary border-b border-[var(--vscode-border-primary)]">
                  <th className="text-center px-1 py-1.5 border-l border-[var(--vscode-border-primary)]">Email</th>
                  <th className="text-center px-1 py-1.5">Phone</th>
                  <th className="text-center px-1 py-1.5">Notes</th>
                  <th className="text-center px-1 py-1.5 border-l border-[var(--vscode-border-primary)]" title="View list and open records">
                    Read
                  </th>
                  <th className="text-center px-1 py-1.5" title="Create or edit">
                    Write
                  </th>
                  <th className="text-center px-1 py-1.5" title="Delete candidate rows">
                    Delete
                  </th>
                  <th className="text-center px-1 py-1.5" title="Applications, notes, and other child objects">
                    Operate children
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-4 text-center vscode-text-tertiary">
                      No active org users found.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => {
                    const a = normalizeCandidateListUserAccess(local.userFieldAccess[u.id]);
                    return (
                      <tr key={u.id} className="border-b border-[var(--vscode-border-primary)] last:border-0">
                        <td className="px-2 py-1.5 align-top">
                          <span className="vscode-text-primary font-medium">{u.display_name}</span>
                          <span className="block vscode-text-tertiary truncate max-w-[200px]">{u.email}</span>
                        </td>
                        {(['viewEmail', 'viewPhone', 'viewNotes'] as const).map((field, idx) => (
                          <td
                            key={field}
                            className={`text-center px-1 py-1 ${idx === 0 ? 'border-l border-[var(--vscode-border-primary)]' : ''}`}
                          >
                            <input
                              type="checkbox"
                              className="rounded"
                              checked={a[field]}
                              onChange={(e) => patchUserAccess(u.id, field, e.target.checked)}
                              aria-label={`${u.display_name} — ${field}`}
                            />
                          </td>
                        ))}
                        {(['canRead', 'canWrite', 'canDelete', 'canOperateChildren'] as const).map((field, i) => (
                          <td
                            key={field}
                            className={`text-center px-1 py-1 ${i === 0 ? 'border-l border-[var(--vscode-border-primary)]' : ''}`}
                          >
                            <input
                              type="checkbox"
                              className="rounded"
                              checked={a[field]}
                              onChange={(e) => patchUserAccess(u.id, field, e.target.checked)}
                              aria-label={`${u.display_name} — ${field}`}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] vscode-text-tertiary mt-1">
            When masking is on, checked users under Email / Phone / Notes can see unmasked values. Read defaults on for new rows;
            clearing Read is for future strict list hiding. Server RBAC still applies for destructive actions.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-[var(--vscode-border-primary)]">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              saveCandidatesTablePolicy(orgId, local);
              onSave(local);
              onClose();
            }}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}
